/** Stream payloads mirrored from rpi_bridge_ble `connection.snapshot().data`. */

export interface BleStreamBase {
  updated_at?: string
}

export interface BleAdcStream extends BleStreamBase {
  adc_raw?: number
  voltage?: number
  raw_hex?: string
  raw_len?: number
  characteristic_uuid?: string
}

export interface BleStateStream extends BleStreamBase {
  state_counter?: number
  raw_hex?: string
}

/** Humai GSR streams (rpi_bridge_ble ble_manager). */
export interface BleSclStream extends BleStreamBase {
  scl_uS?: number
  /** Legacy typo from older Pi builds. */
  slc_uS?: number
}

export interface BleScrStream extends BleStreamBase {
  scr_uS?: number
}

/**
 * Stream `counter` del GSR propio tal cual lo publica la RPi
 * (`ble_manager.py::_build_notification_handler` + `_build_range_measurement`).
 * `state_counter` es metadata de anclaje (detecta drops), NUNCA señal. La
 * medición viene de `gsr_protocol.py::interpret_range_measurement` y sólo
 * trae `measurement_conductance_uS` cuando `measurement_quality === 'valid'`.
 */
export interface BleCounterStream extends BleStreamBase {
  state_counter?: number
  counter_payload_len?: number
  counter_format?: 'empty' | 'legacy_counter' | 'range_measurement'
  range?: number
  range_label?: string
  measurement_adc?: number
  elapsed_since_range_change_ms?: number
  /** Reloj monotónico de la Pico al leer el ADC (firmware >= 2026-09-07). */
  sample_time_ms?: number
  measurement_updated_at?: string
  measurement_quality?: GsrMeasurementQuality
  measurement_voltage_v?: number
  measurement_resistance_ohm?: number
  measurement_conductance_uS?: number
  measurement_relative_uncertainty?: number
  measurement_conductance_error_uS?: number
  measurement_resistance_error_ohm?: number
}

export interface BleHrStream extends BleStreamBase {
  heartrate?: number
  rr_intervals?: number[]
}

export interface BleEcgAccStream extends BleStreamBase {
  samples?: number[]
  sample_count?: number
  timestamp?: number
  samples_truncated?: boolean
}

export type BleDeviceType = 'polar_h10' | 'humai_gsr'

export type BleDeviceData = {
  adc?: BleAdcStream
  state?: BleStateStream
  scl?: BleSclStream
  scr?: BleScrStream
  counter?: BleCounterStream
  hr?: BleHrStream
  ecg?: BleEcgAccStream
  acc?: BleEcgAccStream
  [stream: string]: BleStreamBase | undefined
}

/** Max BLE devices of each type per sensor kit (Quest participant). */
export const BLE_KIT_POLICY: Record<BleDeviceType, { maxPerKit: number }> = {
  humai_gsr: { maxPerKit: 1 },
  polar_h10: { maxPerKit: 1 },
}

/** Alphanumeric kit id (UUID or lab label, e.g. kit-01). */
export const KIT_ID_PATTERN = /^[a-zA-Z0-9_-]{1,128}$/
export const KIT_ID_STORAGE_KEY = 'versports:kit_id'

export function isValidKitId(value: unknown): value is string {
  return typeof value === 'string' && KIT_ID_PATTERN.test(value.trim())
}

export function normalizeKitId(value: unknown): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim()
  return KIT_ID_PATTERN.test(trimmed) ? trimmed : null
}

export function createKitId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `kit-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

/**
 * Physical verification code printed on a sticker on each sensor kit
 * (vrs-docs/34-manual-kit-fisiologico.md §2). A player has to type it before
 * the vr-app is allowed to claim the kit over WS — proves they're holding the
 * physical object, since `kit_id` alone is visible to anyone who can see the
 * kit list. 4 chars, uppercase, alphabet excludes visually ambiguous glyphs
 * (0/O, 1/I/L) so it reads correctly off a small handwritten/printed label.
 * The code itself is generated and owned by the backend (`PiKitBinding.
 * verification_code`) at kit-creation time — this file only validates the
 * shape of what a player types in.
 *
 * Desde el "kit unificado" (mg 2026-09-07) este código de 4 es el FALLBACK:
 * solo vale para placas sin `Kit` de club vinculado. Cuando la placa tiene
 * kit, el código es el `access_code` de 8 chars impreso en la caja — ver
 * KIT_ACCESS_CODE_LENGTH abajo y vrs-docs/34-manual-kit-fisiologico.md §2.
 */
export const KIT_CODE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTUVWXYZ'
export const KIT_CODE_LENGTH = 4

/**
 * Código del kit del club (`Kit.access_code`, backend app/services/kits.py):
 * 8 chars, mismo criterio de glifos sin ambigüedad pero con `L`. Desde el
 * "kit unificado" (mg 2026-09-07) es TAMBIÉN el código que se tipea en el
 * casco para conectarse a los sensores de la placa vinculada a ese kit.
 */
export const KIT_ACCESS_CODE_ALPHABET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ'
export const KIT_ACCESS_CODE_LENGTH = 8

/** Largos que el casco sabe pedir. El backend manda cuál en `code_length`. */
export type KitCodeLength = typeof KIT_CODE_LENGTH | typeof KIT_ACCESS_CODE_LENGTH

/** Un `code_length` desconocido (backend viejo, o payload raro) cae al de 4:
 *  el fallback histórico nunca deja al jugador sin poder tipear nada. */
export function resolveKitCodeLength(value: unknown): KitCodeLength {
  return value === KIT_ACCESS_CODE_LENGTH ? KIT_ACCESS_CODE_LENGTH : KIT_CODE_LENGTH
}

const KIT_CODE_PATTERNS: Record<KitCodeLength, RegExp> = {
  [KIT_CODE_LENGTH]: /^[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{4}$/,
  [KIT_ACCESS_CODE_LENGTH]: /^[23456789ABCDEFGHJKLMNPQRSTUVWXYZ]{8}$/,
}

export const KIT_CODE_PATTERN = KIT_CODE_PATTERNS[KIT_CODE_LENGTH]
export const KIT_ACCESS_CODE_PATTERN = KIT_CODE_PATTERNS[KIT_ACCESS_CODE_LENGTH]

export function kitCodePattern(length: KitCodeLength): RegExp {
  return KIT_CODE_PATTERNS[length] ?? KIT_CODE_PATTERN
}

/**
 * Normaliza (trim + uppercase) y valida contra el largo pedido. Sin `length`
 * acepta CUALQUIERA de los dos: es lo que usa la rehidratación de
 * localStorage, que no sabe si la placa guardada tenía kit vinculado.
 */
export function normalizeKitCode(value: unknown, length?: KitCodeLength): string | null {
  if (typeof value !== 'string') return null
  const trimmed = value.trim().toUpperCase()
  if (length !== undefined) {
    return kitCodePattern(length).test(trimmed) ? trimmed : null
  }
  return KIT_CODE_PATTERN.test(trimmed) || KIT_ACCESS_CODE_PATTERN.test(trimmed)
    ? trimmed
    : null
}

export function loadStoredKitId(): string | null {
  if (typeof localStorage === 'undefined') return null
  try {
    const value = localStorage.getItem(KIT_ID_STORAGE_KEY)
    if (!value) return null
    return normalizeKitId(value)
  } catch {
    return null
  }
}

export function persistKitId(kitId: string): void {
  if (typeof localStorage === 'undefined') return
  try {
    localStorage.setItem(KIT_ID_STORAGE_KEY, kitId)
  } catch {
    /* ignore quota / private mode */
  }
}

export function resolveKitId(urlKitId?: string | null): string {
  const fromUrl = urlKitId ? normalizeKitId(urlKitId) : null
  if (fromUrl) {
    persistKitId(fromUrl)
    return fromUrl
  }
  const stored = loadStoredKitId()
  if (stored) return stored
  const created = createKitId()
  persistKitId(created)
  return created
}

/** True for Pi→Quest payloads scoped to this kit, or global broadcasts without kit_id. */
export function matchesKitScope(payload: Record<string, unknown>, kitId: string): boolean {
  const payloadKit = payload.kit_id
  if (payloadKit === undefined || payloadKit === null || payloadKit === '') return true
  return String(payloadKit) === kitId
}

export function withKitId<T extends Record<string, unknown>>(
  payload: T,
  kitId: string,
): T & { kit_id: string } {
  return { ...payload, kit_id: kitId }
}

export interface BleDeviceSnapshot {
  mac_address: string
  connected: boolean
  name?: string | null
  rpi_id?: string
  kit_id?: string | null
  session_id?: string | null
  device_type?: BleDeviceType | null
  battery_level?: number | null
  streams?: string[]
  active_streams?: string[]
  last_seen?: string | null
  updated_at?: string | null
  rssi?: number | null
  data?: BleDeviceData | null
  status?: 'connected' | 'disconnected' | 'connecting' | 'disconnecting' | string
  last_error?: string | null
}

/** Normalized sample for biofeedback HUD + telemetry `biofeedback.sample`. */
export interface BiofeedbackSample {
  t: number
  hr: number
  hrv: number
  /**
   * `false` cuando `hrv` es el default de render (30 ms) porque la ventana
   * de RR tenia menos de 2 muestras, no una medicion real de RMSSD. El HUD
   * puede seguir mostrando el numero (glow, texto) para no dejar la UI en
   * blanco, pero cualquier cosa que persista o promedie `hrv` tiene que
   * mirar este flag primero. Ver vrs-docs 2c58fcb0 / 10-telemetry.md.
   */
  hrvValid: boolean
  edaScl: number
  edaScrFlag: boolean
}

export interface PhysioSamplePayload {
  t: number
  hr: number
  /**
   * `null` cuando la ventana de RR no alcanzaba para un RMSSD real. El 30 de
   * `BiofeedbackSample.hrv` es un valor de render del HUD y NO sale del
   * dispositivo: lo que se persiste tiene que poder distinguirse de una
   * medicion. Ver `hrv_valid` y 10-telemetry.md §biofeedback.sample.
   */
  hrv: number | null
  /** Espejo de `BiofeedbackSample.hrvValid` en el payload que viaja a telemetria. */
  hrv_valid: boolean
  eda_scl: number
  eda_scr: boolean
}

export { createRrWindow } from './rrWindow'
export {
  COMPANION_MIN_PROTOCOL,
  COMPANION_PROTOCOL,
  COMPANION_REPLAY_PAGE_LIMIT,
  COMPANION_REPLAY_TIMEOUT_MS,
  COMPANION_URL,
  CompanionBluetooth,
  CompanionCharacteristic,
  CompanionDevice,
  CompanionGatt,
  CompanionService,
  canonicalBleUuid,
  type CompanionBluetoothOptions,
  type CompanionHello,
  type CompanionNotification,
  type CompanionNotifyMeta,
  type CompanionReplayReport,
  type CompanionRequestDeviceOptions,
  type CompanionState,
} from './companionBluetooth'

/** RMSSD en ms sobre una lista de RR en ms (>= 2 valores; null si no alcanza). */
export function rmssdFromRr(rr: number[]): number | null {
  if (rr.length < 2) return null
  let sumSq = 0
  for (let i = 1; i < rr.length; i += 1) {
    const prev = rr[i - 1]
    const curr = rr[i]
    if (prev === undefined || curr === undefined) continue
    const diff = curr - prev
    sumSq += diff * diff
  }
  return Math.sqrt(sumSq / (rr.length - 1))
}

const DEVICE_TYPE_LABELS: Record<BleDeviceType, string> = {
  polar_h10: 'Polar H10',
  humai_gsr: 'Humai GSR',
}

let lastStateCounter: number | null = null

function parseDeviceType(value: unknown): BleDeviceType | null {
  if (value === 'polar_h10' || value === 'humai_gsr') return value
  return null
}

const POLAR_NAME_MARKERS = ['polar h10', 'polar h 10', 'polar h9', 'polar oh1']
const GSR_NAME_MARKERS = ['pico2w-gsr', 'humai', 'gsr']

/** Mirrors rpi_bridge_ble `infer_device_type` from advertised BLE name. */
export function inferDeviceTypeFromName(name: string | null | undefined): BleDeviceType | null {
  if (!name) return null
  const normalized = name.trim().toLowerCase()
  if (POLAR_NAME_MARKERS.some((marker) => normalized.includes(marker))) {
    return 'polar_h10'
  }
  if (GSR_NAME_MARKERS.some((marker) => normalized.includes(marker))) {
    return 'humai_gsr'
  }
  return null
}

/**
 * De dónde sale el SCL que devuelve `extractSclMicrosiemens`:
 * - `counter_adc`: conductancia derivada del ADC crudo del `counter`
 *   (`measurement_conductance_uS`, sólo con `measurement_quality === 'valid'`).
 *   Es la medición.
 * - `legacy_pico_kpi`: KPI `scl` que calcula la Pico. NO es medición: usa la
 *   conversión del hardware anterior (`adc_gsr.c`, constantes de Wheatstone) y
 *   con el kit al aire da 8,47 µS contra 0,005 µS desde el ADC. Sólo queda como
 *   fallback para firmware viejo sin `range_measurement`.
 * - `adc_voltage`: build antiquísimo que publicaba `adc.voltage`.
 */
export type EdaSource = 'counter_adc' | 'legacy_pico_kpi' | 'adc_voltage'

/** True si el snapshot trae un `counter` en formato range_measurement (haya o
 * no muestra válida ahora): a partir de ahí el KPI legacy no vale como fuente. */
export function hasCounterMeasurement(data: BleDeviceData | null | undefined): boolean {
  const counter = data?.counter
  if (!counter) return false
  return counter.counter_format === 'range_measurement' || typeof counter.measurement_quality === 'string'
}

/** Conductancia válida del ADC del `counter`, o null (inválida / firmware viejo). */
export function extractCounterConductance(
  data: BleDeviceData | null | undefined,
): number | null {
  const counter = data?.counter
  if (!counter || counter.measurement_quality !== 'valid') return null
  if (!gsrHasSettled(counter.elapsed_since_range_change_ms)) return null
  const value = counter.measurement_conductance_uS
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

export function extractEdaSource(data: BleDeviceData | null | undefined): EdaSource | null {
  if (!data) return null
  if (hasCounterMeasurement(data)) return 'counter_adc'
  const scl = data.scl
  if (scl && (typeof scl.scl_uS === 'number' || typeof scl.slc_uS === 'number')) return 'legacy_pico_kpi'
  if (data.adc && typeof data.adc.voltage === 'number') return 'adc_voltage'
  return null
}

/**
 * SCL en µS. Prioridad: conductancia válida del ADC (`counter`), y sólo si el
 * firmware no manda `range_measurement`, el KPI legacy `scl` (ver `EdaSource`).
 * Con `counter` presente pero inválido (saturado / en cero) devuelve null: no
 * hay número real y el legacy no lo reemplaza.
 */
export function extractSclMicrosiemens(
  data: BleDeviceData | null | undefined,
): number | null {
  if (!data) return null
  if (hasCounterMeasurement(data)) return extractCounterConductance(data)
  const scl = data.scl
  if (scl) {
    if (typeof scl.scl_uS === 'number') return scl.scl_uS
    if (typeof scl.slc_uS === 'number') return scl.slc_uS
  }
  const adc = data.adc
  if (adc && typeof adc.voltage === 'number') return adc.voltage
  return null
}

/**
 * SCR en µS, o null cuando no hay fuente válida de fásico — y el consumidor lo
 * trata como "sin fásico", nunca como 0. El firmware ya no aporta SCR: con
 * `counter` en formato range_measurement el KPI `scr` de la Pico se ignora
 * (`(uint16_t)(scr*1000)` sobre un float con signo: toda desviación negativa
 * envuelve o se lee 0). El fásico real se deriva aguas abajo sobre la serie de
 * conductancia (`tonicPhasic` acá, `dsp_engine.py::derive_phasic` en la Pi).
 */
export function extractScrMicrosiemens(
  data: BleDeviceData | null | undefined,
): number | null {
  if (!data || hasCounterMeasurement(data)) return null
  const scr = data.scr
  if (scr && typeof scr.scr_uS === 'number') return scr.scr_uS
  return null
}

export function extractStateCounter(
  data: BleDeviceData | null | undefined,
): number | null {
  if (!data) return null
  const counter = data.counter?.state_counter ?? data.state?.state_counter
  return typeof counter === 'number' ? counter : null
}

/** Human-readable label — prefers BLE name, then device_type, then MAC. */
export function deviceDisplayName(
  device: Pick<BleDeviceSnapshot, 'name' | 'mac_address' | 'device_type'>,
): string {
  const trimmed = device.name?.trim()
  if (trimmed) return trimmed
  if (device.device_type) return DEVICE_TYPE_LABELS[device.device_type]
  return device.mac_address
}

/**
 * Maps a BLE device snapshot (RPi registry) into a biofeedback sample.
 * Returns null when insufficient signal is present.
 */
export function mapBleSnapshotToBiofeedback(
  device: BleDeviceSnapshot,
): BiofeedbackSample | null {
  const data = device.data
  if (!data) return null

  let hr: number | null = null
  // 30 es el default de RENDER cuando no hay >= 2 RR en la ventana — nunca
  // una medicion. `hrvValid` es lo que distingue una cosa de la otra para
  // quien consuma esta muestra (ver comentario en BiofeedbackSample.hrvValid).
  let hrv = 30
  let hrvValid = false
  let edaScl = 0
  let edaScrFlag = false

  const hrStream = data.hr
  if (hrStream && typeof hrStream.heartrate === 'number') {
    hr = hrStream.heartrate
    const rr = hrStream.rr_intervals
    if (Array.isArray(rr) && rr.length >= 2) {
      const rmssd = rmssdFromRr(rr)
      if (rmssd !== null) {
        hrv = rmssd
        hrvValid = true
      }
    }
  }

  const scl = extractSclMicrosiemens(data)
  if (scl !== null) edaScl = scl

  const stateCounter = extractStateCounter(data)
  if (stateCounter !== null) {
    if (lastStateCounter !== null && stateCounter !== lastStateCounter) {
      edaScrFlag = true
    }
    lastStateCounter = stateCounter
  }

  if (hr === null && edaScl === 0) return null

  return {
    t: Date.now(),
    hr: hr ?? 0,
    hrv,
    hrvValid,
    edaScl,
    edaScrFlag,
  }
}

export function biofeedbackToPhysioPayload(
  sample: BiofeedbackSample,
): PhysioSamplePayload {
  return {
    t: sample.t,
    hr: sample.hr,
    hrv: sample.hrvValid ? sample.hrv : null,
    hrv_valid: sample.hrvValid,
    eda_scl: sample.edaScl,
    eda_scr: sample.edaScrFlag,
  }
}

/** Truncate heavy stream arrays for UI / cloud relay display. */
export function truncateBleDataForDisplay(
  data: BleDeviceData | null | undefined,
  maxSamples = 5,
): BleDeviceData {
  if (!data) return {}
  const out: BleDeviceData = {}
  for (const [stream, raw] of Object.entries(data)) {
    if (!raw || typeof raw !== 'object') continue
    const item = { ...raw } as BleEcgAccStream
    if (
      (stream === 'ecg' || stream === 'acc') &&
      Array.isArray(item.samples) &&
      item.samples.length > maxSamples
    ) {
      item.samples = item.samples.slice(-maxSamples)
      item.samples_truncated = true
    }
    out[stream] = item
  }
  return out
}

export function mergeBleDevice(
  prev: BleDeviceSnapshot,
  patch: Partial<BleDeviceSnapshot>,
): BleDeviceSnapshot {
  const nextData =
    patch.data !== undefined
      ? { ...(prev.data ?? {}), ...(patch.data ?? {}) }
      : prev.data

  return {
    ...prev,
    ...patch,
    name: patch.name !== undefined ? patch.name : prev.name,
    data: nextData,
    streams: patch.streams ?? patch.active_streams ?? prev.streams ?? prev.active_streams,
  }
}

export function snapshotToBleDevice(snapshot: Record<string, unknown>): BleDeviceSnapshot {
  const mac = String(snapshot.mac_address ?? '')
  const activeStreams = snapshot.active_streams
  return {
    mac_address: mac,
    connected: Boolean(snapshot.connected),
    name: typeof snapshot.name === 'string' ? snapshot.name : null,
    kit_id: typeof snapshot.kit_id === 'string' ? snapshot.kit_id : null,
    device_type: parseDeviceType(snapshot.device_type),
    status: typeof snapshot.status === 'string' ? snapshot.status : undefined,
    streams: Array.isArray(activeStreams)
      ? (activeStreams as string[])
      : Array.isArray(snapshot.desired_streams)
        ? (snapshot.desired_streams as string[])
        : [],
    active_streams: Array.isArray(activeStreams) ? (activeStreams as string[]) : [],
    data: (snapshot.data as BleDeviceData) ?? {},
    battery_level:
      typeof snapshot.battery_level === 'number' ? snapshot.battery_level : null,
    updated_at: typeof snapshot.updated_at === 'string' ? snapshot.updated_at : null,
    last_error: typeof snapshot.last_error === 'string' ? snapshot.last_error : null,
  }
}

export function upsertBleDeviceList(
  devices: BleDeviceSnapshot[],
  patch: BleDeviceSnapshot,
): BleDeviceSnapshot[] {
  const idx = devices.findIndex((d) => d.mac_address === patch.mac_address)
  if (idx === -1) return [...devices, patch]
  const next = [...devices]
  next[idx] = mergeBleDevice(devices[idx]!, patch)
  return next
}

export function applyBleLinkStatus(
  devices: BleDeviceSnapshot[],
  payload: Record<string, unknown>,
): BleDeviceSnapshot[] {
  if (Array.isArray(payload.linked)) {
    let next = devices
    for (const item of payload.linked as Record<string, unknown>[]) {
      const snap = item.device
        ? snapshotToBleDevice(item.device as Record<string, unknown>)
        : snapshotToBleDevice(item)
      next = upsertBleDeviceList(next, {
        ...snap,
        mac_address: String(item.mac_address ?? snap.mac_address),
        // El snapshot del dispositivo es la verdad del enlace: un bridge
        // anterior al 2026-09-17 contesta `connected: true` apenas se le
        // PIDE conectar, aunque el runner siga en "reconnecting" porque el
        // sensor no se anuncia. Sólo sin snapshot se cree en el flag.
        connected: item.device ? snap.connected : Boolean(item.connected),
        name: typeof item.name === 'string' ? item.name : snap.name,
      })
    }
    return next
  }

  const mac = String(payload.mac_address ?? '')
  if (!mac) return devices

  const snap = payload.device
    ? snapshotToBleDevice(payload.device as Record<string, unknown>)
    : snapshotToBleDevice(payload)

  return upsertBleDeviceList(devices, {
    ...snap,
    mac_address: mac,
    // Misma regla: con snapshot manda el snapshot (ver arriba).
    connected: payload.device ? snap.connected : Boolean(payload.connected ?? snap.connected),
  })
}

/* -------------------------------------------------------------------------- */
/* dsp_metrics — indice de regulacion en vivo, computado en la Pi.            */
/* -------------------------------------------------------------------------- */

/**
 * Shape EXACTO de `metrics` en el mensaje `{"type":"dsp_metrics", "kit_id":
 * ..., "metrics": {...}}` que manda la Pi — ver
 * `vrs-sbc/rpi_bridge_ble/dsp_engine.py::LiveDSPEngine.compute_kit_metrics`
 * (repo aparte, solo lectura, NO tocar). Espejo 1:1 de ese dict, no inventado.
 *
 * `r_live_cardio*` es el indice EN VIVO de la Pi, con baseline movil. No
 * confundir con `r_cardio*` del silver offline (`regulation_metrics.py`), que
 * es por ciclo y con baseline congelado de sesion: son metricas distintas y
 * nunca deben mezclarse en el mismo campo.
 *
 * Ambas apps (web-app KNOW y vr-app) consumen este mismo mensaje, por eso el
 * tipo vive aca y no duplicado en cada una.
 */
export interface CardioLiveBlock {
  r_live_cardio: number | null
  quality: string
  reason: string | null
  components: Record<string, number | null>
  rmssd: {
    rmssd_ms: number | null
    reason: string | null
    window_s: number
    /** Summed RR actually in the current window (bridges >= rmssd-primary-v2). */
    effective_window_ms?: number
    /** `full` (>= 60 s effective) | `short` | null without RMSSD. */
    duration_quality?: 'full' | 'short' | null
    rr_intervals_in_window: number
    /** Raw intervals repaired by the artifact correction (>= rmssd-primary-v3). */
    rr_corrected_in_window?: number
  }
}

export interface EdaLiveBlock {
  r_live_cardio_eda: number | null
  quality: string
  reason: string | null
  /**
   * Indice de regulacion SOLO EDA (`dsp_engine.compute_eda_only_index`).
   * Existe con GSR y SIN Polar, que es justo donde `r_live_cardio_eda` es
   * null. Mide el eje simpatico y NO tiene eje vagal: es un fallback, no un
   * reemplazo, y nunca se promedia con los `r_live_cardio*`. Ausente en
   * bridges anteriores al 2026-09-07, por eso opcional.
   */
  r_live_eda?: number | null
  /** `good` | `marginal` | `insufficient`. */
  r_live_eda_quality?: string | null
  /** `non_responder`, `insufficient_eda_window`, `no_gsr_linked`, ... */
  r_live_eda_reason?: string | null
  components: Record<string, number | null>
  scl: {
    value: number | null
    mean: number | null
    median: number | null
    mad: number | null
    n: number
    reason: string | null
  }
  dscl_dt: {
    value_uS_per_s: number | null
    reason: string | null
    window_s?: number
    n?: number
  }
  /** `counter_adc` | `legacy_pico_kpi` | null (Pi field; absent in old bridges). */
  eda_source?: string | null
  scr_rate: {
    rate_per_min: number | null
    reason: string | null
    detection_quality: string
    detection_note?: string
  }
}

export interface DspMetrics {
  formula_version: string
  /** Effective live cardiac estimator settings; absent on legacy bridges. */
  cardio_config?: import('./hrBeat').CardioEngineConfig
  /** Original bridge version when its measured features were projected to v2. */
  source_formula_version?: string
  kit_id: string
  updated_at: number
  cardio: CardioLiveBlock
  eda: EdaLiveBlock
}

export {
  REGULATION_CONTROL_VERSION, PI_DSP_FORMULA_VERSION, HR_BEAT_VERSION, HR_BEAT_V1_VERSION, HR_BEAT_VERSIONS, CANONICAL_HR_BEAT_VERSION,
  RMSSD_LOG_UNIT, isHrBeatVersion, rmssdControl, controlDspMetrics,
} from './regulationControl'
export {
  HR_BEAT_ANCHOR_FAST_DEFAULT,
  HR_BEAT_ANCHOR_MAX,
  HR_BEAT_ANCHOR_MIN,
  HR_BEAT_ANCHOR_SLOW_DEFAULT,
  HR_BEAT_ANCHOR_MODE_DEFAULT,
  HR_BEAT_SD_MIN_BLOCKS,
  HR_BEAT_SD_UNIT_MAX,
  HR_BEAT_SD_UNIT_MIN,
  HR_BEAT_SD_UNITS_DEFAULT,
  HR_BEAT_SD_UNITS_MAX,
  HR_BEAT_SD_UNITS_MIN,
  HR_BEAT_ARTIFACT_PREV_N,
  HR_BEAT_ARTIFACT_THRESHOLD,
  HR_BEAT_FAST_MEDIAN_N,
  HR_BEAT_MIN_REFERENCE_MS,
  HR_BEAT_MIN_SLOW_BEATS,
  HR_BEAT_MIN_SLOW_FRACTION,
  HR_BEAT_REFERENCE_S,
  HR_BEAT_SLOW_WINDOW_S_DEFAULT,
  HR_BEAT_SLOW_WINDOW_S_MAX,
  HR_BEAT_SLOW_WINDOW_S_MIN,
  createHrBeatEngine,
  createCanonicalHrBeatEngine,
  canonicalHrBeatConfig,
  type CanonicalHrBeatConfig,
  type CanonicalHrBeatOptions,
  type CanonicalHrBeatEngine,
  hrBeatConfig,
  type CardioEngineConfig,
  type HrBeatConfig,
  type HrBeatAnchorMode,
  type HrBeatConfigV1,
  type HrBeatEngine,
  type HrBeatOptions,
} from './hrBeat'
export {
  DSP_EVAL_INTERVAL_MS,
  DSP_RMSSD_BASELINE_S,
  DSP_RMSSD_CURRENT_S,
  DSP_RR_MAX_MS,
  DSP_RR_MIN_MS,
  DSP_RR_STALE_S,
  createDspCardioEngine,
  dspComputeCardioIndex,
  dspComputeRmssd,
  type DspCardioEngine,
  type DspCardioVersion,
  type DspCardioConfig,
  type DspCardioOptions,
} from './dspCardio'
export {
  RR_ARTIFACT_MATCH_TOLERANCE,
  RR_ARTIFACT_MEDIAN_HALF_WINDOW,
  RR_ARTIFACT_THRESHOLD,
  RR_ECTOPIC_JUMP,
  RR_ECTOPIC_PAIR_TOLERANCE,
  RR_MAX_CORRECTED_FRACTION,
  correctRrArtifacts,
  type RrCorrection,
  type RrCorrectedBeat,
} from './rrArtifacts'

/** Narrowing del `metrics` crudo de un mensaje `dsp_metrics` del WS. */
export function isDspMetrics(value: unknown): value is DspMetrics {
  if (!value || typeof value !== 'object') return false
  const m = value as Record<string, unknown>
  return typeof m.cardio === 'object' && m.cardio !== null
}

/* -------------------------------------------------------------------------- */
/* GATT Heart Rate Measurement (0x2A37)                                        */
/* -------------------------------------------------------------------------- */

export interface ParsedHrMeasurement {
  hr: number
  /** RR intervals en ms, más viejo primero (puede venir vacío). */
  rrMs: number[]
}

/**
 * Parser del characteristic Heart Rate Measurement (0x2A37) tal como lo manda
 * el Polar H10 por Web Bluetooth (vr-app Polar directo, web-app Device Panel):
 *   byte 0 = flags: bit0 → HR en uint16 LE (si no, uint8); bit3 → Energy
 *   Expended (uint16) antes de los RR; bit4 → hay RR-intervals al final
 *   (uint16 LE cada uno, en unidades de 1/1024 s). Los RR se devuelven en ms
 *   sin redondear: la resolución nativa (1000/1024 ≈ 0,977 ms) queda visible
 *   y es lo que audita `vrs-data` (`ph.rr.resolution`).
 */
export function parseHeartRateMeasurement(view: DataView): ParsedHrMeasurement {
  const flags = view.getUint8(0)
  const hr16 = (flags & 0x01) !== 0
  const hasRr = (flags & 0x10) !== 0
  let offset = 1
  let hr: number
  if (hr16) {
    hr = view.getUint16(offset, true)
    offset += 2
  } else {
    hr = view.getUint8(offset)
    offset += 1
  }
  if ((flags & 0x08) !== 0) offset += 2
  const rrMs: number[] = []
  if (hasRr) {
    for (; offset + 1 < view.byteLength; offset += 2) {
      rrMs.push((view.getUint16(offset, true) / 1024) * 1000)
    }
  }
  return { hr, rrMs }
}

/* -------------------------------------------------------------------------- */
/* GATT de la placa GSR (Pico W / Pico 2W) — servicio custom                   */
/* -------------------------------------------------------------------------- */

/**
 * UUIDs del servicio custom que expone la placa GSR. Espejo EXACTO de
 * `vrs-sbc/rpi_bridge_ble/settings.py` (repo aparte): si allá cambian, acá hay
 * que cambiarlos igual o el navegador no encuentra el servicio.
 *
 * A diferencia del Polar H10 —que usa el perfil estándar `heart_rate`
 * (0x180D/0x2A37) y por eso Chrome lo conoce por nombre— este servicio es de
 * 128 bits y hay que nombrarlo explícitamente en `requestDevice`
 * (`filters` / `optionalServices`). Los dos sensores son BLE: la única
 * diferencia es ésa.
 */
export const GSR_SERVICE_UUID = '12345678-1234-5678-1234-567812345678'
export const GSR_SCL_CHAR_UUID = '12345678-1234-5678-1234-567812345679'
export const GSR_SCR_CHAR_UUID = '12345678-1234-5678-1234-56781234567a'
export const GSR_COUNTER_CHAR_UUID = '12345678-1234-5678-1234-56781234567b'
export const GSR_CONTROL_CHAR_UUID = '12345678-1234-5678-1234-56781234567c'
export const GSR_BATTERY_STATUS_CHAR_UUID = '12345678-1234-5678-1234-56781234567d'
export const GSR_BATTERY_LEVEL_CHAR_UUID = '00002a19-0000-1000-8000-00805f9b34fb'

/** Nombre BLE anunciado por la placa. Legacy: NO afirma el modelo de MCU. */
export const GSR_ADVERTISED_NAME = 'Pico2W-GSR'

/** Magic byte que marca un `counter` con medición de rango (no el legacy). */
export const GSR_RANGE_MEASUREMENT_MAGIC = 0xb1

export const GSR_ADC_VREF_VOLTS = 3.3
export const GSR_ADC_FULL_SCALE = 4096
/** Vref nominal del frente de transimpedancia (`33 §2.1`). */
export const GSR_NOMINAL_VREF_VOLTS = 0.5

export interface GsrRangeCalibration {
  rangeId: number
  label: string
  feedbackResistanceOhm: number
  saturationVoltageV: number
}

/** Los cuatro rangos de realimentación de la placa (`33 §2.2`). */
export const GSR_RANGE_CALIBRATIONS: Record<number, GsrRangeCalibration> = {
  1: { rangeId: 1, label: '47k', feedbackResistanceOhm: 47_000, saturationVoltageV: 2.65 },
  2: { rangeId: 2, label: '330k', feedbackResistanceOhm: 330_000, saturationVoltageV: 2.65 },
  3: { rangeId: 3, label: '1M', feedbackResistanceOhm: 1_000_000, saturationVoltageV: 2.65 },
  4: { rangeId: 4, label: '3.3M', feedbackResistanceOhm: 3_300_000, saturationVoltageV: 2.65 },
}

/** Incertidumbres 1-sigma independientes, con los defaults conservadores del
 * bridge (5 % de tolerancia de Rf through-hole, Vref medido a 0,01 V, 2 % de
 * referencia de ADC y cuantización). */
export interface GsrUncertainty {
  vrefUncertaintyV: number
  adcReferenceRelativeUncertainty: number
  adcCodeUncertaintyLsb: number
  feedbackToleranceRelative: number
}

export const GSR_DEFAULT_UNCERTAINTY: GsrUncertainty = {
  vrefUncertaintyV: 0.01,
  adcReferenceRelativeUncertainty: 0.02,
  adcCodeUncertaintyLsb: 1 / Math.sqrt(12),
  feedbackToleranceRelative: 0.05,
}

export type GsrMeasurementQuality =
  | 'valid'
  | 'near_zero'
  | 'saturated_high'
  | 'unknown_range'

export interface GsrRangeMeasurement {
  rangeId: number
  rangeLabel: string | null
  adcCode: number
  voltageV: number
  quality: GsrMeasurementQuality
  /** Sólo con `quality === 'valid'`. */
  resistanceOhm: number | null
  conductanceUS: number | null
  relativeUncertainty: number | null
}

/** `Vadc = code * ADC_VREF / 4096` (`GSR_PROTOCOL.md`). */
export function gsrAdcToVoltage(adcCode: number): number {
  return (adcCode / GSR_ADC_FULL_SCALE) * GSR_ADC_VREF_VOLTS
}

/**
 * Port 1:1 de `gsr_protocol.py::interpret_range_measurement`.
 *
 * `R_skin = Vref * Rf / Vadc`. Los extremos del ADC se reportan como estados
 * de calidad y NUNCA se convierten en una resistencia fabricada: un valor
 * saturado con un número al lado es peor que sin número.
 */
export function interpretGsrRangeMeasurement(
  rangeId: number,
  adcCode: number,
  vrefVolts: number = GSR_NOMINAL_VREF_VOLTS,
  uncertainty: GsrUncertainty = GSR_DEFAULT_UNCERTAINTY,
): GsrRangeMeasurement {
  const voltageV = gsrAdcToVoltage(adcCode)
  const calibration = GSR_RANGE_CALIBRATIONS[rangeId]
  const base = {
    rangeId,
    rangeLabel: calibration?.label ?? null,
    adcCode,
    voltageV,
    resistanceOhm: null,
    conductanceUS: null,
    relativeUncertainty: null,
  }
  if (!calibration) return { ...base, quality: 'unknown_range' }
  if (voltageV <= 0) return { ...base, quality: 'near_zero' }
  if (voltageV >= calibration.saturationVoltageV) {
    return { ...base, quality: 'saturated_high' }
  }

  const resistanceOhm = (vrefVolts * calibration.feedbackResistanceOhm) / voltageV
  const conductanceUS = 1_000_000 / resistanceOhm
  const adcVoltageUncertainty = Math.sqrt(
    ((GSR_ADC_VREF_VOLTS / GSR_ADC_FULL_SCALE) * uncertainty.adcCodeUncertaintyLsb) ** 2 +
      (voltageV * uncertainty.adcReferenceRelativeUncertainty) ** 2,
  )
  const relativeUncertainty = Math.sqrt(
    (uncertainty.vrefUncertaintyV / vrefVolts) ** 2 +
      uncertainty.feedbackToleranceRelative ** 2 +
      (adcVoltageUncertainty / voltageV) ** 2,
  )
  return {
    ...base,
    quality: 'valid',
    resistanceOhm,
    conductanceUS,
    relativeUncertainty,
  }
}

export interface GsrCounterPayload {
  format: 'empty' | 'legacy_counter' | 'range_measurement'
  stateCounter: number | null
  rangeId: number | null
  adcCode: number | null
  elapsedSinceRangeChangeMs: number | null
  /** ms del reloj monotónico de la Pico al leer el ADC (firmware >= 2026-09-07).
   * NO es UTC. */
  sampleTimeMs: number | null
}

/**
 * Port 1:1 de `gsr_protocol.py::parse_counter_payload`. Layout:
 * `[state_counter, 0xB1, range, adc(LE16), elapsed(LE32), (sample_time(LE32))]`.
 */
export function parseGsrCounterPayload(view: DataView): GsrCounterPayload {
  const empty: GsrCounterPayload = {
    format: 'empty',
    stateCounter: null,
    rangeId: null,
    adcCode: null,
    elapsedSinceRangeChangeMs: null,
    sampleTimeMs: null,
  }
  if (view.byteLength === 0) return empty

  const stateCounter = view.getUint8(0)
  if (view.byteLength >= 9 && view.getUint8(1) === GSR_RANGE_MEASUREMENT_MAGIC) {
    return {
      format: 'range_measurement',
      stateCounter,
      rangeId: view.getUint8(2),
      adcCode: view.getUint16(3, true),
      elapsedSinceRangeChangeMs: view.getUint32(5, true),
      sampleTimeMs: view.byteLength >= 13 ? view.getUint32(9, true) : null,
    }
  }
  return { ...empty, format: 'legacy_counter', stateCounter }
}

/**
 * SCL / SCR en µS. La Pico manda un entero little-endian de largo variable en
 * milésimas de µS (`ble_manager.py`: `int.from_bytes(data, 'little') / 1000`).
 */
export function parseGsrMicrosiemens(view: DataView): number | null {
  if (view.byteLength === 0) return null
  let value = 0
  for (let i = view.byteLength - 1; i >= 0; i -= 1) {
    value = value * 256 + view.getUint8(i)
  }
  return value / 1000
}

export const GSR_BATTERY_STATE_NAMES: Record<number, string> = {
  0: 'FULL',
  1: 'NORMAL',
  2: 'LOW',
  3: 'CRITICAL',
}

export interface GsrBatteryStatus {
  stateCode: number
  state: string
  voltageMv: number
}

/** Port de `ble_manager.py::_parse_battery_status_payload`:
 * `[state_code, voltage_mv (LE16)]`. */
export function parseGsrBatteryStatus(view: DataView): GsrBatteryStatus | null {
  if (view.byteLength < 3) return null
  const stateCode = view.getUint8(0)
  return {
    stateCode,
    state: GSR_BATTERY_STATE_NAMES[stateCode] ?? `UNKNOWN_${stateCode}`,
    voltageMv: view.getUint16(1, true),
  }
}

// ---------------------------------------------------------------------------
// Tónico / fásico sobre la conductancia del ADC (compartido web-app + vr-app).
// ---------------------------------------------------------------------------

/**
 * Descomposición tónico / fásico de la conductancia de piel del lado cliente,
 * sobre las muestras VÁLIDAS del `counter` (ADC crudo → conductancia vía
 * `interpretGsrRangeMeasurement`). Reemplaza a los KPI `scl`/`scr` que calcula
 * la Pico, que no sirven como medición (ver `EdaSource`).
 *
 * Tónico = mediana de la ventana (robusta a los escalones de contacto de
 * electrodos secos); fásico = última muestra − tónico, con signo. Es el MISMO
 * estimador que `dsp_engine.py::derive_phasic` (Pi, en vivo) y
 * `regulation_metrics.py::_phasic_series` (backend, por ciclo), para que los
 * tres caminos sean comparables. Vista de banco, no el estimador del protocolo.
 */
export interface ConductanceSample {
  /** Date.now() al recibir el notify. */
  t: number
  /** µS, sólo muestras `quality === 'valid'`. */
  uS: number
}

/** Ventana del tónico (ms). Igual a `TONIC_WINDOW_S` de la Pi y el backend. */
export const TONIC_WINDOW_MS = 30_000
/** Con menos muestras válidas en la ventana, el tónico no se reporta. */
export const TONIC_MIN_SAMPLES = 20

export function median(values: number[]): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const mid = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[mid]! : (sorted[mid - 1]! + sorted[mid]!) / 2
}

/** Devuelve la ventana recortada a los últimos `windowMs` respecto de `now`. */
export function trimWindow(
  samples: ConductanceSample[],
  now: number,
  windowMs: number = TONIC_WINDOW_MS,
): ConductanceSample[] {
  const cutoff = now - windowMs
  let start = 0
  while (start < samples.length && samples[start]!.t < cutoff) start += 1
  return start === 0 ? samples : samples.slice(start)
}

export interface TonicPhasic {
  tonicUS: number | null
  phasicUS: number | null
  /** Muestras válidas que sostienen el tónico. */
  windowSamples: number
}

export function tonicPhasic(window: ConductanceSample[]): TonicPhasic {
  const count = window.length
  if (count < TONIC_MIN_SAMPLES) return { tonicUS: null, phasicUS: null, windowSamples: count }
  const tonic = median(window.map((s) => s.uS))
  const last = window[count - 1]!.uS
  return {
    tonicUS: tonic,
    phasicUS: tonic === null ? null : last - tonic,
    windowSamples: count,
  }
}

/* -------------------------------------------------------------------------- */
/* Calidad de la señal GSR — resumen puro sobre una ventana de muestras        */
/* -------------------------------------------------------------------------- */

/** Una muestra cruda del `counter`, ya decodificada, con su hora de llegada. */
export interface GsrSample {
  /** `Date.now()` de la notificación (hora de llegada al browser, no de origen). */
  t: number
  rangeId: number
  adcCode: number
  quality: GsrMeasurementQuality
  conductanceUS: number | null
  /** ms desde el último cambio de rango que reportó el firmware. */
  elapsedSinceRangeChangeMs: number | null
  /** SCL de la Pico vigente en esa muestra, si la placa lo expone. */
  sclUS: number | null
}

/** Cadencia nominal del firmware (`pico_capabilities.sample_interval_ms`). */
export const GSR_EXPECTED_SAMPLE_INTERVAL_MS = 100
/** Espera tras un cambio de rango (`DEFAULT_SETTLE_MS`): por debajo, la muestra
 * todavía no asentó y su valor no es confiable. */
export const GSR_SETTLE_MS = 110

/** Missing timing stays compatible with legacy snapshots; raw timing is preserved. */
export function gsrHasSettled(elapsedMs: number | null | undefined): boolean {
  return elapsedMs == null || (Number.isFinite(elapsedMs) && elapsedMs >= GSR_SETTLE_MS)
}

export function isUsableGsrMeasurement(
  measurement: Pick<GsrRangeMeasurement, 'quality' | 'conductanceUS'>,
  elapsedMs: number | null | undefined,
): boolean {
  return measurement.quality === 'valid' && measurement.conductanceUS !== null &&
    Number.isFinite(measurement.conductanceUS) && measurement.conductanceUS >= 0 && gsrHasSettled(elapsedMs)
}

export interface GsrQualitySummary {
  samples: number
  durationS: number
  /** Muestras por segundo medidas contra el reloj del browser. */
  effectiveHz: number | null
  medianIntervalMs: number | null
  /** Intervalo peor caso del percentil 95: mide jitter, no un outlier suelto. */
  p95IntervalMs: number | null
  /** Intervalos > 3× la mediana: notifies perdidos (BLE es best-effort). */
  gaps: number
  validSamples: number
  saturatedSamples: number
  nearZeroSamples: number
  /** Fracción de muestras sin resistencia utilizable, 0..1. */
  invalidFraction: number
  rangeSwitches: number
  /** Muestras entregadas antes del settle tras un cambio de rango. */
  settleViolations: number
  /**
   * Ruido de ADC como RMS de las DIFERENCIAS SUCESIVAS dentro de un mismo
   * rango (mismo criterio que el RMSSD del cardio): la diferencia sucesiva
   * cancela la deriva lenta de la piel, que es señal, y deja el temblor
   * muestra-a-muestra, que es ruido. En LSB del ADC de 12 bits.
   */
  adcNoiseRmsLsb: number | null
  /** El mismo ruido llevado a µS, para leerlo contra la magnitud de un SCR. */
  conductanceNoiseRmsUS: number | null
  /** Mediana de la conductancia válida, µS. */
  medianConductanceUS: number | null
  /**
   * Mediana de `scl_uS / conductancia(ADC)`. Los dos NO tienen por qué dar 1
   * —`GSR_PROTOCOL.md` dice que el SCL de la Pico es un KPI tónico y no la
   * reconstrucción de la resistencia instantánea— pero un ratio estable es
   * evidencia de que miden lo mismo y uno que se va lejos o deriva es una
   * bandera. `null` si la placa no expone SCL.
   */
  sclOverAdcRatio: number | null
}

function percentile(values: number[], p: number): number | null {
  if (values.length === 0) return null
  const sorted = [...values].sort((a, b) => a - b)
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1))
  return sorted[idx]!
}

/**
 * Resumen de calidad sobre una ventana de muestras del `counter`.
 *
 * Existe porque "se ve bien" no es un criterio: para aceptar una placa hay que
 * poder decir a qué cadencia real llega, cuánto ruido tiene muestra a muestra,
 * qué fracción sale saturada y si el autorango está golpeando de un rango a
 * otro. Todo se computa sobre la ventana que se le pase; el llamador decide
 * cuánta historia mirar.
 */
export function summarizeGsrQuality(samples: GsrSample[]): GsrQualitySummary {
  const empty: GsrQualitySummary = {
    samples: samples.length,
    durationS: 0,
    effectiveHz: null,
    medianIntervalMs: null,
    p95IntervalMs: null,
    gaps: 0,
    validSamples: 0,
    saturatedSamples: 0,
    nearZeroSamples: 0,
    invalidFraction: 0,
    rangeSwitches: 0,
    settleViolations: 0,
    adcNoiseRmsLsb: null,
    conductanceNoiseRmsUS: null,
    medianConductanceUS: null,
    sclOverAdcRatio: null,
  }
  if (samples.length === 0) return empty

  const intervals: number[] = []
  for (let i = 1; i < samples.length; i += 1) {
    intervals.push(samples[i]!.t - samples[i - 1]!.t)
  }
  const medianIntervalMs = median(intervals)
  const gaps =
    medianIntervalMs && medianIntervalMs > 0
      ? intervals.filter((v) => v > medianIntervalMs * 3).length
      : 0

  const first = samples[0]!
  const last = samples[samples.length - 1]!
  const durationS = (last.t - first.t) / 1000

  let saturatedSamples = 0
  let nearZeroSamples = 0
  let rangeSwitches = 0
  let settleViolations = 0
  const validConductance: number[] = []
  const sclRatios: number[] = []
  // Diferencias sucesivas SOLO entre muestras válidas, consecutivas y del
  // mismo rango: un cambio de rango produce un salto de ADC que es del
  // instrumento, no de la piel, y contarlo como ruido lo infla.
  const adcDiffs: number[] = []
  const conductanceDiffs: number[] = []

  for (let i = 0; i < samples.length; i += 1) {
    const s = samples[i]!
    if (s.quality === 'saturated_high') saturatedSamples += 1
    if (s.quality === 'near_zero') nearZeroSamples += 1
    if (!gsrHasSettled(s.elapsedSinceRangeChangeMs)) {
      settleViolations += 1
    }
    if (isUsableGsrMeasurement(s, s.elapsedSinceRangeChangeMs)) {
      validConductance.push(s.conductanceUS!)
      if (s.sclUS !== null && s.conductanceUS! > 0) sclRatios.push(s.sclUS / s.conductanceUS!)
    }
    const prev = i > 0 ? samples[i - 1]! : null
    if (!prev) continue
    if (prev.rangeId !== s.rangeId) {
      rangeSwitches += 1
      continue
    }
    if (isUsableGsrMeasurement(prev, prev.elapsedSinceRangeChangeMs) &&
        isUsableGsrMeasurement(s, s.elapsedSinceRangeChangeMs)) {
      adcDiffs.push(s.adcCode - prev.adcCode)
      if (prev.conductanceUS !== null && s.conductanceUS !== null) {
        conductanceDiffs.push(s.conductanceUS - prev.conductanceUS)
      }
    }
  }

  const rms = (values: number[]): number | null => {
    if (values.length === 0) return null
    let sumSq = 0
    for (const v of values) sumSq += v * v
    return Math.sqrt(sumSq / values.length)
  }

  const validSamples = validConductance.length
  return {
    samples: samples.length,
    durationS,
    effectiveHz: durationS > 0 ? (samples.length - 1) / durationS : null,
    medianIntervalMs,
    p95IntervalMs: percentile(intervals, 0.95),
    gaps,
    validSamples,
    saturatedSamples,
    nearZeroSamples,
    invalidFraction: (samples.length - validSamples) / samples.length,
    rangeSwitches,
    settleViolations,
    adcNoiseRmsLsb: rms(adcDiffs),
    conductanceNoiseRmsUS: rms(conductanceDiffs),
    medianConductanceUS: median(validConductance),
    sclOverAdcRatio: median(sclRatios),
  }
}

/** Cabecera + filas CSV de una ventana de muestras, para analizar afuera
 * (una fila por notificación del `counter`, no un muestreo de `state.json`
 * como el `vrs-gsr-capture` de la Pi). */
export function gsrSamplesToCsv(samples: GsrSample[]): string {
  const header = [
    't_iso',
    't_ms',
    'range_id',
    'adc_code',
    'voltage_v',
    'resistance_ohm',
    'conductance_uS',
    'quality',
    'elapsed_since_range_change_ms',
    'scl_uS',
  ].join(',')
  const rows = samples.map((s) => {
    const m = interpretGsrRangeMeasurement(s.rangeId, s.adcCode)
    return [
      new Date(s.t).toISOString(),
      s.t,
      s.rangeId,
      s.adcCode,
      m.voltageV.toFixed(6),
      m.resistanceOhm != null ? m.resistanceOhm.toFixed(1) : '',
      m.conductanceUS != null ? m.conductanceUS.toFixed(6) : '',
      s.quality,
      s.elapsedSinceRangeChangeMs ?? '',
      s.sclUS != null ? s.sclUS.toFixed(6) : '',
    ].join(',')
  })
  return [header, ...rows].join('\n')
}
export * from './polarPmd'
